import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { H1, H2, H3, Paragraph, Text } from 'components/core/Typography';
import { Card } from 'components/core/Card';
import { UnitIcon } from 'components/unit/UnitIcon';
import {
	SIZE_CATEGORIES,
	categorizeUnit,
} from 'components/facility/FilterableUnitListV2/FilterableUnitList';

import './UnitSizeGuideV2.scss';

/* eslint-disable max-len */
// NOTE: the `sqft` prop on the items are part of the experiment
const UNIT_SIZES = [
	{
		display: '5’ x 5’',
		bucket: '5x5',
		squareFootage: 25,
		looksLike: 'A small closet',
		goodFor:
			"The contents of one large closet. This size is ideal for seasonal decorations, personal items, gardening equipment, camping gear, children's toys, or off-season clothing.",
	},
	{
		display: '5’ x 10’',
		bucket: '5x10',
		squareFootage: 50,
		looksLike: 'A walk-in closet',
		goodFor:
			'The contents of one bedroom or dorm room, such as a mattress set, couch, table, chairs, and boxes.',
	},
	{
		display: '5’ x 15’',
		bucket: '5x15',
		squareFootage: 75,
		looksLike: 'A large walk-in closet',
		goodFor:
			'The contents of two rooms, such as large dining sets, desks, cabinets, mattress sets, and washer/dryer. ',
	},
	{
		display: '10’ x 10’',
		bucket: '10x10',
		squareFootage: 100,
		looksLike: 'A small bedroom',
		goodFor:
			'The contents of one large bedroom, such as bedroom/living sets, refrigerator, large drawers, shelves, and multiple boxes.',
	},
	{
		display: '10’ x 15’',
		bucket: '10x15',
		squareFootage: 150,
		looksLike: 'A small one-car garage',
		goodFor:
			'The contents of three to four rooms, such as multiple bedroom sets, large appliances and patio sets. May also fit smaller cars, boats, and other small vehicles.',
	},
	{
		display: '10’ x 20’',
		bucket: '10x20',
		squareFootage: 200,
		looksLike: 'A standard one-car garage',
		goodFor:
			'The contents of a two to three-bedroom house, such as multiple bedroom sets, and large furniture. Can also fit trucks, trailers, and other mid-size vehicles.',
	},
	{
		display: '10’ x 30’',
		bucket: '10x30',
		squareFootage: 300,
		looksLike:
			'A large car garage. Often the largest unit size at most facilities.',
		goodFor:
			'The contents of a four- to six-bedroom house, such as mattress sets, large furniture, major appliances, seasonal items, outdoor equipment, and bikes. Will fit most vehicles.',
	},
];

function UnitSizeCard({ unit }) {
	return (
		<Card className="unit-size-guide__unit-size-card">
			<div className="unit-size-guide__unit-size-header">
				<div className="unit-size-guide__unit-size-info">
					<H1
						size="semiLarge"
						weight="ultraBold"
					>
						{unit.display}
					</H1>
					<Card className="unit-size-guide__unit-size-sqft-card">
						<H2 weight="ultraBold">
							{unit.squareFootage} <span>SQ FT</span>
						</H2>
					</Card>
				</div>
				<UnitIcon unitSize={`${unit.bucket}-storage`} />
			</div>
			<div className="unit-size-guide__unit-size-info">
				<H3 weight="ultraBold">Looks Like:</H3>
				<Paragraph weight="standard">{unit.looksLike}</Paragraph>
			</div>
			<div className="unit-size-guide__unit-size-info">
				<H3 weight="ultraBold">What Will Fit:</H3>
				<Paragraph weight="standard">{unit.goodFor}</Paragraph>
			</div>
		</Card>
	);
}

export default function UnitSizeGuideV2({ className }) {
	const [filter, setFilter] = useState('mini'); 

	const filteredUnits = UNIT_SIZES.filter(
			(unit) => categorizeUnit(unit) === filter
	);

	return (
		<div className={classnames('unit-size-guide', className)}>
			<div className='unit-size-guide__header'>
				{SIZE_CATEGORIES.map((category) => (
					<div
						key={category.key}
						className={classnames('unit-size-guide__tab-button', {
							'unit-size-guide__tab-button--active':
								filter === category.key,
						})}
						onClick={() => setFilter(category.key)}
					>
						{category.label}
					</div>
				))}
			</div>
			<div className="unit-size-guide__tab-content">
				{filteredUnits.map((unit) => (
					<UnitSizeCard
						key={unit.bucket}
						unit={unit}
					/>
				))}
			</div>
		</div>
	);
}

UnitSizeCard.propTypes = {
	unit: PropTypes.shape({
		display: PropTypes.string.isRequired,
		bucket: PropTypes.string.isRequired,
		squareFootage: PropTypes.number.isRequired,
		looksLike: PropTypes.string.isRequired,
		goodFor: PropTypes.string.isRequired,
	}).isRequired,
};

UnitSizeGuideV2.propTypes = {
	className: PropTypes.string,
};
